import { useEffect, useRef, useState } from 'react';
import { authService } from '@/services/auth.service';
import { isUploadInProgress } from '@/stores/uploadActivityStore';

// ADR-046 (QA #30): the console's half of the idle timeout. The server closes
// a session whose refresh token is unused for 30 minutes; this hook (a) keeps
// the 15-minute access token fresh while the user is active, (b) warns one
// minute before the idle deadline, and (c) signs out at the deadline so the
// user sees a clear message instead of a surprise 401 later. The server clock
// (last_used_at on refresh) remains the security boundary.
//
// ADR-054 D2: an upload in progress — including one paused waiting for the
// network — is activity. While one runs the hook never warns and never signs
// out; the idle clock restarts when the upload ends. The server keeps the
// session for it too, but only within UPLOAD_RESUME_WINDOW (default 4h).

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
export const IDLE_WARNING_MS = 29 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 30 * 1000;
// Refresh the access token every 12 minutes while active (token TTL 15m).
const ACTIVE_REFRESH_MS = 12 * 60 * 1000;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

export interface IdleTimerState {
  /** true from the 29-minute mark until activity or logout */
  warning: boolean;
  /** dismiss the warning: counts as activity and refreshes the token */
  stay: () => void;
}

export function useIdleTimer(enabled: boolean, onTimeout: () => void): IdleTimerState {
  const lastActivityRef = useRef<number>(Date.now());
  const [warning, setWarning] = useState(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) return;

    let lastRecorded = 0;
    const markActivity = () => {
      const now = Date.now();
      if (now - lastRecorded < ACTIVITY_THROTTLE_MS) return;
      lastRecorded = now;
      lastActivityRef.current = now;
      setWarning(false);
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));
    const onVisibility = () => {
      if (document.visibilityState === 'visible') markActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Idle watchdog (checked every 15s): warn at 29m, sign out at 30m —
    // unless an upload is in progress (ADR-054), which counts as activity.
    const watchdog = window.setInterval(() => {
      if (isUploadInProgress()) {
        lastActivityRef.current = Date.now();
        setWarning(false);
        return;
      }
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        onTimeoutRef.current();
      } else if (idle >= IDLE_WARNING_MS) {
        setWarning(true);
      }
    }, 15 * 1000);

    // Active-only refresh: a user who is working never sees the access token
    // lapse; an idle user is deliberately not refreshed, so the server-side
    // idle window can close the session.
    const refresher = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current < IDLE_TIMEOUT_MS) {
        void authService.refreshSession();
      }
    }, ACTIVE_REFRESH_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(watchdog);
      window.clearInterval(refresher);
    };
  }, [enabled]);

  const stay = () => {
    lastActivityRef.current = Date.now();
    setWarning(false);
    void authService.refreshSession();
  };

  return { warning, stay };
}
